import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    MenuItem,
    TextField, Typography
} from '@mui/material';
import { ModalActionProps } from 'models/AutoModals';
import DropSheet from 'components/dropZone/DropSheet';
import Api from 'utils/api';


type UploadSpreadsheetDialogProps = {
    requiredColumns: string[],
    arbitraryColumns: string[],
} & ModalActionProps<{ file: File, cols: {[k: string]: number} }>

const UploadSpreadsheetDialog = ({ requiredColumns, arbitraryColumns, onFinish }: UploadSpreadsheetDialogProps) => {
    const {t} = useTranslation('cars');
    const [ file, setFile ] = useState<File>();
    const [ titles, setTitles ] = useState<{pos: number, title: string}[]>();

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        onFinish({
            cols: Object.fromEntries([...new FormData(event.currentTarget).entries()].map(e => [e[0], e[1] === '' ? -1 : Number(e[1])])),
            file: file!,
        });
    };

    const getNames = (file: File) => {
        setFile(file);
        Api.util.sheetColNames(file).then(res => setTitles(res.data.map((t, i) => ({pos: i, title: t})).filter(t => t.title)))
    };

    const guessColumn = (col: string) => {
        const split = col.replaceAll('_', ' ');
        let title = titles?.find(t => t.title.trim().toLowerCase().replaceAll('_', ' ') === split);

        if (!title && col === 'chassis_number') {
            title = titles?.find(t => t.title.toLowerCase().startsWith('chassis'));
        }

        return title ? title.pos : '';
    };

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                {!file && (
                    <>
                        <strong>Step 1: Upload file</strong>
                        <DropSheet upload={getNames} title="xls, xlsx, csv"/>

                        <Typography color="text.disabled">Tip: You can prepare spreadsheet by changing the column names with those used here</Typography>
                    </>
                )}

                {file && !titles &&
                    <Box>Preparing...</Box>
                }

                {titles && (
                    <>
                        <strong>Step 2: Define column names</strong>
                        {requiredColumns.map(col => (
                            <TextField
                                key={col}
                                id={col}
                                name={col}
                                label={col.replaceAll('_', ' ')}
                                fullWidth
                                required
                                select
                                defaultValue={guessColumn(col)}
                            >
                                {titles?.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                            </TextField>
                        ))}
                        {arbitraryColumns.map(col => (
                            <TextField
                                key={col}
                                id={col}
                                name={col}
                                label={col.replaceAll('_', ' ')}
                                fullWidth
                                select
                                defaultValue={guessColumn(col)}
                            >
                                {titles?.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                            </TextField>
                        ))}
                    </>
                )}
            </DialogContent>

            {titles && (
                <DialogActions>
                    <Button type="submit">{t('upload')}</Button>
                </DialogActions>
            )}
        </Box>
    );
};

export default UploadSpreadsheetDialog;
